class ConceptCarousel extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.track = this.querySelector('[data-carousel-track]');
    this.slides = Array.from(this.querySelectorAll('[data-carousel-slide]'));
    this.status = this.querySelector('[data-carousel-status]');
    this.index = Math.max(0, this.slides.findIndex((slide) => slide.hasAttribute('data-active')));
    this.previousControl = this.querySelector('[data-carousel-prev]');
    this.nextControl = this.querySelector('[data-carousel-next]');
    this.previousControl?.addEventListener('click', () => this.show(this.index - 1), { signal });
    this.nextControl?.addEventListener('click', () => this.show(this.index + 1), { signal });
    this.querySelectorAll('[data-carousel-go]').forEach((control) => {
      control.addEventListener('click', () => this.show(Number(control.dataset.carouselGo)), { signal });
    });
    if (!this.hasAttribute('tabindex')) this.tabIndex = 0;
    this.setAttribute('aria-roledescription', 'carousel');
    this.addEventListener('keydown', (event) => {
      if (event.defaultPrevented || event.target.closest('input, textarea, select, [role="tab"], [contenteditable="true"]')) return;
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      if (event.key === 'ArrowLeft') this.show(this.index - 1);
      if (event.key === 'ArrowRight') this.show(this.index + 1);
      if (event.key === 'Home') this.show(0);
      if (event.key === 'End') this.show(this.slides.length - 1);
    }, { signal });
    this.track?.addEventListener('pointerdown', (event) => this.onPointerDown(event), { signal });
    this.track?.addEventListener('pointermove', (event) => this.onPointerMove(event), { signal });
    this.track?.addEventListener('pointerup', (event) => this.onPointerEnd(event), { signal });
    this.track?.addEventListener('pointercancel', (event) => this.onPointerEnd(event, true), { signal });
    this.track?.addEventListener('click', (event) => {
      if (!this.suppressClick) return;
      event.preventDefault();
      event.stopImmediatePropagation();
    }, { capture: true, signal });
    this.show(this.index, false);
    if (this.track && 'ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(() => this.show(this.index, false));
      this.resizeObserver.observe(this.track);
    }
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.resizeObserver?.disconnect();
    window.clearTimeout(this.motionTimer);
  }

  onPointerDown(event) {
    if (!this.track || event.button !== 0 || this.slides.length < 2 || this.maxIndex === 0) return;
    this.drag = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      lastX: event.clientX,
      lastTime: performance.now(),
      velocity: 0,
      axis: null,
    };
    this.suppressClick = false;
  }

  onPointerMove(event) {
    if (!this.drag || event.pointerId !== this.drag.pointerId || !this.track) return;
    const deltaX = event.clientX - this.drag.startX;
    const deltaY = event.clientY - this.drag.startY;
    if (!this.drag.axis) {
      if (Math.max(Math.abs(deltaX), Math.abs(deltaY)) < 8) return;
      this.drag.axis = Math.abs(deltaX) > Math.abs(deltaY) ? 'x' : 'y';
      if (this.drag.axis === 'y') return;
      this.track.setPointerCapture?.(event.pointerId);
      this.toggleAttribute('data-carousel-dragging', true);
      this.track.style.transition = 'none';
    }
    if (this.drag.axis !== 'x') return;
    event.preventDefault();
    const now = performance.now();
    const elapsed = Math.max(1, now - this.drag.lastTime);
    this.drag.velocity = (event.clientX - this.drag.lastX) / elapsed;
    this.drag.lastX = event.clientX;
    this.drag.lastTime = now;
    const resistance = (this.index === 0 && deltaX > 0)
      || (this.index === this.maxIndex && deltaX < 0) ? 0.28 : 1;
    this.drag.offset = deltaX * resistance;
    this.track.style.translate = `${this.drag.offset}px 0`;
  }

  onPointerEnd(event, cancelled = false) {
    if (!this.drag || event.pointerId !== this.drag.pointerId || !this.track) return;
    const delta = this.drag.axis === 'x' ? event.clientX - this.drag.startX : 0;
    const projected = delta + this.drag.velocity * 160;
    const threshold = Math.min(72, Math.max(40, this.clientWidth * 0.14));
    const moved = this.drag.axis === 'x' && Math.abs(delta) >= 8;
    let nextIndex = this.index;
    if (!cancelled && Math.abs(projected) >= threshold) {
      nextIndex += projected < 0 ? 1 : -1;
    }
    nextIndex = Math.max(0, Math.min(this.maxIndex, nextIndex));
    if (moved) {
      // Start the release animation at the exact dragged position, including rail gaps.
      this.track.style.transition = 'none';
      this.track.style.transform = `translateX(${-(this.offset || 0) + (this.drag.offset ?? delta)}px)`;
      this.track.style.translate = '0 0';
      void this.track.offsetWidth;
    }
    this.drag = null;
    this.toggleAttribute('data-carousel-dragging', false);
    this.suppressClick = moved;
    this.show(nextIndex, nextIndex !== this.index, moved);
    if (moved) window.setTimeout(() => { this.suppressClick = false; }, 80);
  }

  measureLayout() {
    const boxes = this.slides.map((slide) => slide.getBoundingClientRect());
    const origin = boxes[0]?.left || 0;
    const width = this.track?.clientWidth || 0;
    const positions = boxes.map((box) => ({ start: box.left - origin, end: box.right - origin }));
    const maxOffset = Math.max(0, (positions.at(-1)?.end || 0) - width);
    // A rail may expose several cards at once. Stop at its real end, not a blank final page.
    const lastStart = positions.findIndex((position) => position.start >= maxOffset - 1);
    const maxIndex = width ? Math.max(0, lastStart < 0 ? positions.length - 1 : lastStart) : this.slides.length - 1;
    return { width, positions, maxOffset, maxIndex };
  }

  show(nextIndex, announce = true, settle = false) {
    if (!this.slides.length) return;
    const layout = this.measureLayout();
    this.maxIndex = layout.maxIndex;
    const previousIndex = this.index;
    const safeIndex = Math.max(0, Math.min(this.maxIndex, nextIndex));
    this.offset = Math.min(layout.positions[safeIndex]?.start || 0, layout.maxOffset);
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const animate = !reducedMotion && this.track && (safeIndex !== previousIndex || settle);
    window.clearTimeout(this.motionTimer);
    if (this.track) {
      if (animate) {
        this.toggleAttribute('data-carousel-moving', true);
        this.track.style.transitionProperty = 'transform, translate';
        this.track.style.transitionDuration = '560ms';
        this.track.style.transitionTimingFunction = 'cubic-bezier(0.22, 1, 0.36, 1)';
      } else {
        this.track.style.transition = 'none';
      }
    }
    this.index = safeIndex;
    this.slides.forEach((slide, index) => {
      const active = index === this.index;
      const position = layout.positions[index];
      const visible = layout.width
        ? position.end > this.offset + 1 && position.start < this.offset + layout.width - 1
        : active;
      slide.toggleAttribute('data-active', active);
      slide.toggleAttribute('inert', !visible);
      slide.setAttribute('aria-hidden', String(!visible));
    });
    this.querySelectorAll('[data-carousel-go]').forEach((control) => {
      control.setAttribute('aria-current', String(Number(control.dataset.carouselGo) === this.index));
    });
    if (this.previousControl) this.previousControl.disabled = this.index === 0;
    if (this.nextControl) this.nextControl.disabled = this.index === this.maxIndex;
    if (this.track) {
      this.track.style.setProperty('--carousel-index', this.index);
      this.track.style.transform = `translateX(${-this.offset}px)`;
    }
    if (this.status && announce) {
      this.status.textContent = '';
      requestAnimationFrame(() => {
        this.status.textContent = `Item ${this.index + 1} of ${this.slides.length}`;
      });
    }
    requestAnimationFrame(() => {
      if (this.track) this.track.style.translate = '0 0';
    });
    if (animate) {
      this.motionTimer = window.setTimeout(() => {
        this.toggleAttribute('data-carousel-moving', false);
        this.track?.style.removeProperty('transition-property');
        this.track?.style.removeProperty('transition-duration');
        this.track?.style.removeProperty('transition-timing-function');
      }, 640);
    } else {
      requestAnimationFrame(() => this.track?.style.removeProperty('transition'));
    }
  }
}

class ConceptDrawer extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this.openers = document.querySelectorAll(`[data-drawer-open="${this.id}"]`);
    this.openers.forEach((opener) => opener.addEventListener('click', () => this.open(opener)));
    this.querySelectorAll('[data-drawer-close]').forEach((closer) => closer.addEventListener('click', () => this.close()));
    this.dialog?.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.close();
    });
    this.dialog?.addEventListener('close', () => {
      document.documentElement.classList.remove('concept-drawer-open');
      this.returnFocus?.focus();
    });
  }

  open(opener) {
    if (!this.dialog) return;
    this.returnFocus = opener;
    document.querySelectorAll('dialog[open]').forEach((dialog) => {
      if (dialog !== this.dialog) dialog.close();
    });
    this.dialog.showModal();
    document.documentElement.classList.add('concept-drawer-open');
  }

  close() {
    this.dialog?.close();
    document.documentElement.classList.remove('concept-drawer-open');
  }
}

class ConceptTabs extends HTMLElement {
  connectedCallback() {
    this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
    this.tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => this.select(index));
      tab.addEventListener('keydown', (event) => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        let next = index;
        if (event.key === 'ArrowLeft') next = (index - 1 + this.tabs.length) % this.tabs.length;
        if (event.key === 'ArrowRight') next = (index + 1) % this.tabs.length;
        if (event.key === 'Home') next = 0;
        if (event.key === 'End') next = this.tabs.length - 1;
        this.select(next);
        this.tabs[next].focus();
      });
    });
    this.select(Math.max(0, this.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true')));
  }

  select(index) {
    this.tabs.forEach((tab, tabIndex) => {
      const selected = tabIndex === index;
      tab.setAttribute('aria-selected', String(selected));
      tab.tabIndex = selected ? 0 : -1;
      const panel = document.getElementById(tab.getAttribute('aria-controls'));
      if (panel) panel.hidden = !selected;
    });
  }
}

class ConceptStickyBuy extends HTMLElement {
  connectedCallback() {
    const sourceId = this.dataset.stickySource;
    this.source = sourceId ? document.getElementById(sourceId) : null;
    if (this.source?.hasAttribute('data-purchase-unavailable')) return;
    if (!this.source || !('IntersectionObserver' in window)) return;
    this.observer = new IntersectionObserver(([entry]) => {
      this.toggleAttribute('data-visible', !entry.isIntersecting);
    }, { threshold: 0 });
    this.observer.observe(this.source);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }
}

class ConceptBuyOptions extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    this.addEventListener('change', (event) => {
      const select = this.querySelector('[data-custom-quantity]');
      if (event.target.matches('input[name="purchase_option"]') && select) select.value = event.target.value;
      if (event.target.matches('input[name="purchase_option"], [data-custom-quantity]')) this.sync();
    }, { signal: this.controller.signal });
    for (const radio of this.querySelectorAll('input[name="purchase_option"]')) radio.disabled = false;
    this.sync();
  }

  sync() {
    const selected = this.querySelector('[data-custom-quantity]');
    if (!selected) return;
    const quantity = selected.value;
    if (!Number.isInteger(Number(quantity)) || Number(quantity) < 1 || Number(quantity) > 10) return;
    for (const radio of this.querySelectorAll('input[name="purchase_option"]')) radio.checked = radio.value === quantity;
    for (const purchase of document.querySelectorAll('[data-buy-section]')) {
      if (purchase.dataset.buySection !== this.dataset.buySection) continue;
      const quantityInput = purchase.querySelector('[data-buy-quantity]');
      if (quantityInput) quantityInput.value = quantity;
      const button = purchase.querySelector('[data-buy-sticky-button]');
      if (button) button.textContent = quantity === '3' ? button.dataset.labelFamily
        : quantity === '1' ? button.dataset.labelSingle
          : (button.dataset.labelCustom || button.dataset.labelSingle).replace('__quantity__', quantity);
    }
  }

  disconnectedCallback() {
    this.controller?.abort();
  }
}

class ConceptAnnouncement extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.items = Array.from(this.querySelectorAll('[data-announcement-item]'));
    this.toggle = this.querySelector('[data-announcement-pause], .concept-announcement__toggle');
    if (this.items.length < 2 || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      if (this.toggle) this.toggle.hidden = true;
      return;
    }
    this.index = 0;
    this.paused = false;
    if (!this.toggle) {
      this.toggle = document.createElement('button');
      this.toggle.className = 'concept-announcement__toggle';
      this.append(this.toggle);
    }
    this.toggle.hidden = false;
    this.toggle.type = 'button';
    this.toggle.textContent = 'Pause';
    this.toggle.setAttribute('aria-label', 'Pause rotating announcements');
    this.toggle.addEventListener('click', () => this.toggleRotation(), { signal });
    this.start();
    this.addEventListener('pointerenter', () => this.stop(), { signal });
    this.addEventListener('pointerleave', () => {
      if (!this.paused) this.start();
    }, { signal });
    this.addEventListener('focusin', () => this.stop(), { signal });
    this.addEventListener('focusout', () => {
      if (!this.paused) this.start();
    }, { signal });
  }

  start() {
    this.stop();
    this.timer = window.setInterval(() => {
      this.items[this.index].hidden = true;
      this.index = (this.index + 1) % this.items.length;
      this.items[this.index].hidden = false;
    }, 5000);
  }

  stop() {
    window.clearInterval(this.timer);
  }

  toggleRotation() {
    this.paused = !this.paused;
    this.toggle.textContent = this.paused ? 'Play' : 'Pause';
    this.toggle.setAttribute('aria-label', `${this.paused ? 'Resume' : 'Pause'} rotating announcements`);
    if (this.paused) this.stop(); else this.start();
  }

  disconnectedCallback() {
    this.stop();
    this.controller?.abort();
  }
}

customElements.define('concept-carousel', ConceptCarousel);
customElements.define('concept-drawer', ConceptDrawer);
customElements.define('concept-tabs', ConceptTabs);
customElements.define('concept-sticky-buy', ConceptStickyBuy);
customElements.define('concept-buy-options', ConceptBuyOptions);
customElements.define('concept-announcement', ConceptAnnouncement);
